import {
  AssessmentStateV2,
  DashboardFilters,
  EngagementAssessmentV2Run,
  HeatmapCell,
  LatestRunPerEngagement,
  ManagerId,
  ManagerPillarHeatmapData,
  ManagerSinHeatmapData,
  PILLARS_V2,
  PillarResultV2,
  PillarV2,
  PillarsSinsScatterData,
  ScatterPoint,
  SINS_V2,
  SinV2,
  V2SinResult,
} from "../../types/engagementAssessmentV2Types";
import { stateScore } from "./constants";

export const latestRunPerEngagement = (
  runs: EngagementAssessmentV2Run[],
): EngagementAssessmentV2Run | null => {
  if (runs.length === 0) return null;
  return runs.reduce((acc, r) =>
    Date.parse(r.assessed_at) > Date.parse(acc.assessed_at) ? r : acc,
  );
};

export const pillarScoreFromState = (
  state: AssessmentStateV2,
): number | null => stateScore[state];

export const applyDashboardFilters = (
  latestRuns: LatestRunPerEngagement[],
  filters: DashboardFilters,
): LatestRunPerEngagement[] => {
  const managerSet =
    filters.managerIds.length > 0 ? new Set(filters.managerIds) : null;
  const themeSet =
    filters.themes.length > 0 ? new Set(filters.themes) : null;
  const startMs = Date.parse(filters.dateRange.start);
  const endMs = Date.parse(filters.dateRange.end);

  return latestRuns.filter((run) => {
    if (managerSet !== null && !managerSet.has(run.manager_id)) return false;
    if (themeSet !== null && !run.themes.some((t) => themeSet.has(t))) {
      return false;
    }
    const ts = Date.parse(run.assessed_at);
    if (ts < startMs || ts > endMs) return false;
    return true;
  });
};

const collectManagers = (
  latestRuns: LatestRunPerEngagement[],
): { id: ManagerId; name: string }[] => {
  const seen = new Map<ManagerId, string>();
  for (const run of latestRuns) {
    if (!seen.has(run.manager_id)) {
      seen.set(run.manager_id, run.manager_name);
    }
  }
  return Array.from(seen, ([id, name]) => ({ id, name }));
};

const groupByManager = (
  latestRuns: LatestRunPerEngagement[],
): Map<ManagerId, LatestRunPerEngagement[]> => {
  const byManager = new Map<ManagerId, LatestRunPerEngagement[]>();
  for (const run of latestRuns) {
    const list = byManager.get(run.manager_id);
    if (list) list.push(run);
    else byManager.set(run.manager_id, [run]);
  }
  return byManager;
};

const findPillar = (
  pillars: PillarResultV2[],
  pillar: PillarV2,
): PillarResultV2 | undefined => pillars.find((p) => p.pillar === pillar);

const findSin = (sins: V2SinResult[], sin: SinV2): V2SinResult | undefined =>
  sins.find((s) => s.sin === sin);

const meanPillarScore = (pillars: PillarResultV2[]): number => {
  let sum = 0;
  let count = 0;
  for (const pillar of PILLARS_V2) {
    const entry = findPillar(pillars, pillar);
    if (!entry) continue;
    const score = pillarScoreFromState(entry.state);
    if (score !== null) {
      sum += score;
      count += 1;
    }
  }
  return count > 0 ? sum / count : NaN;
};

export const computeManagerPillarHeatmap = (
  filteredLatestRuns: LatestRunPerEngagement[],
): ManagerPillarHeatmapData => {
  const managers = collectManagers(filteredLatestRuns);
  const runsByManager = groupByManager(filteredLatestRuns);

  const cells: HeatmapCell<PillarV2>[] = [];
  for (const { id: managerId } of managers) {
    const managerRuns = runsByManager.get(managerId) ?? [];
    for (const pillar of PILLARS_V2) {
      let sum = 0;
      let count = 0;
      for (const run of managerRuns) {
        const entry = findPillar(run.assessment.pillars, pillar);
        if (!entry) continue;
        const score = pillarScoreFromState(entry.state);
        if (score !== null) {
          sum += score;
          count += 1;
        }
      }
      cells.push({
        row: managerId,
        col: pillar,
        value: count > 0 ? sum / count : NaN,
        count,
      });
    }
  }
  return { managers, cells };
};

export const computeManagerSinHeatmap = (
  filteredLatestRuns: LatestRunPerEngagement[],
): ManagerSinHeatmapData => {
  const managers = collectManagers(filteredLatestRuns);
  const runsByManager = groupByManager(filteredLatestRuns);

  const cells: HeatmapCell<SinV2>[] = [];
  for (const { id: managerId } of managers) {
    const managerRuns = runsByManager.get(managerId) ?? [];
    const count = managerRuns.length;
    for (const sin of SINS_V2) {
      let incidents = 0;
      for (const run of managerRuns) {
        const entry = findSin(run.assessment.sins, sin);
        if (entry && entry.evidence_refs.length > 0) incidents += 1;
      }
      cells.push({
        row: managerId,
        col: sin,
        value: count > 0 ? incidents / count : NaN,
        count,
      });
    }
  }
  return { managers, cells };
};

export const computeScatterPoints = (
  filteredLatestRuns: LatestRunPerEngagement[],
): PillarsSinsScatterData => {
  const points: ScatterPoint[] = filteredLatestRuns.map((run) => {
    const x = meanPillarScore(run.assessment.pillars);
    const y = run.assessment.sins.reduce(
      (n, s) => (s.evidence_refs.length > 0 ? n + 1 : n),
      0,
    );
    return {
      unitId: run.engagement_record_id,
      unitLabel: run.engagement_record_id,
      x,
      y,
      count: 1,
    };
  });
  return { points };
};
